/**
 * Hook for the miniInitialise mutation.
 *
 * Mini-initialisation creates new sections/PPs/locations detected by the
 * scheduling-sync flow. Runs as an ARQ background job and reuses the same
 * progress-polling mechanism as full project initialisation
 * (`useInitialisationProgress`).
 */

import { gql, useMutation } from "@apollo/client";

const MINI_INITIALISE = gql`
  mutation MiniInitialise($input: MiniInitInput!) {
    miniInitialise(input: $input) {
      success
      message
      operationId
    }
  }
`;

export interface MiniInitSpaceInput {
  schedulingSpaceId: string;
  includeOsakohteenTarkastus: boolean;
  includeMalliasennustarkastus: boolean;
  includeMestanVastaanotto: boolean;
  includeTyovaiheenvastaanotto: boolean;
  includeLiitedokumentit: boolean;
}

export interface MiniInitSpaceResponsibleInput {
  /** External scheduling-space id (NOT a DB location id). The worker resolves
   *  it to a Location and sets/creates the section-location's responsible. */
  schedulingSpaceId: string;
  responsibleUserId: string;
}

export interface MiniInitSectionInput {
  sectionCode: string;
  sectionName: string;
  templateWorkSectionId?: string | null;
  procurementPackageCode: string;
  /** Plot scoping for the section's PP lookup. Required when the project has
   *  duplicate procurement_package_codes across plots — otherwise the backend
   *  attaches the section to a random plot's PP. */
  plotSchedulingSpaceId?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  spaces: MiniInitSpaceInput[];
  /** Staged section-location responsibles, keyed by schedulingSpaceId. */
  spaceResponsibles?: MiniInitSpaceResponsibleInput[];
}

export interface MiniInitProcurementPackageInput {
  templateProcurementPackageId: string;
  code: string;
  name?: string | null;
  responsibleUserId?: string | null;
  plotSchedulingSpaceId: string;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  includeSuunnitelmiinPerehtyminen: boolean;
  includeAliurakkaPerehtyminen: boolean;
  includeMestanValmistelu: boolean;
  includeTaloudellinenLoppuselvitys: boolean;
}

export interface MiniInitLocationInput {
  schedulingSpaceId: string;
  name: string;
  parentSchedulingSpaceId?: string | null;
  spaceType?: string | null;
}

/** A confirmed deletion: type is "section_location" | "section" | "location",
 *  id is the DB id of the entity to soft-delete. */
export interface MiniInitDeletionInput {
  type: string;
  id: string;
}

export interface MiniInitInput {
  projectId: string;
  operationId?: string | null;
  newLocations: MiniInitLocationInput[];
  newProcurementPackages: MiniInitProcurementPackageInput[];
  newSections: MiniInitSectionInput[];
  deletions: MiniInitDeletionInput[];
  inspectionMatrixId?: string | null;
  inspectionTemplateOwnerProjectId?: string | null;
}

export interface MiniInitResult {
  success: boolean;
  message: string;
  operationId?: string | null;
}

export function useMiniInitialise() {
  const [mutate, { loading, error }] = useMutation<{
    miniInitialise: MiniInitResult;
  }>(MINI_INITIALISE, {
    refetchQueries: ["GetProjects"],
  });

  const miniInitialise = async (
    input: MiniInitInput,
  ): Promise<MiniInitResult | null> => {
    const result = await mutate({ variables: { input } });
    return result.data?.miniInitialise ?? null;
  };

  return { miniInitialise, loading, error };
}
